import { useEffect, useState, type ReactNode } from 'react';
import { App as CapacitorApp } from '@capacitor/app';

import { UpdateBanner } from './UpdateBanner';
import { UpdateRequiredScreen } from './UpdateRequiredScreen';
import { getLatestVersion, getUpdateReminderDays, subscribeAppConfig } from '@/lib/appConfig';
import { isStoreBuild } from '@/lib/platform';
import { STORE_URL } from '@/lib/storeLinks';
import { appPlatform, decideStartup, type StartupDecision } from '@/lib/updateGate';
import { laterHides, rememberLater, shouldShowBanner, type LaterRecord } from '@/lib/updateLater';
import { useAuthStore } from '@/stores/auth';

/**
 * Пускает в приложение — или показывает «обновите приложение», или предлагает обновиться плашкой.
 *
 * ⚠️ ПО УМОЛЧАНИЮ ПУСКАЕТ. Пока решение не принято (настройки ещё едут), человек
 * видит обычное приложение, а не заглушку: сеть может быть медленной, и держать
 * ученика перед пустым экраном из-за служебной проверки нельзя.
 *
 * ⚠️ ЗАЧЕМ ЭТО ВООБЩЕ. Правило простое: сервер обязан понимать старые
 * приложения. Но 01.09.2026 его нарушили — приложение стало слать заголовок,
 * которого сервер не разрешал, и обновившиеся остались без входа. Если однажды
 * сломается наоборот, человека надо честно попросить обновиться, а не оставлять
 * с бесконечными ошибками.
 *
 * ⚠️ ПРОВЕРКА НЕ ТОЛЬКО НА СТАРТЕ (15.10.2026). До этого решение принималось при запуске и через 4 с, а однажды
 * запертый экран не снимался до второго холодного запуска (CHECK-2026-10-05 §4). Теперь решение пересчитывается при
 * каждом приезде настроек (`subscribeAppConfig`; при возврате в приложение их перечитывает `lib/freshness.ts`) и
 * при самом возврате (`appStateChange`): срок «Позже» мог истечь, а настройки — не поменяться. Сервер убрал
 * требование — экран снимается сам.
 *
 * Мягкая плашка «Вышла новая версия» — поверх приложения, а не вместо него; «Позже» прячет её на
 * `limits.update_reminder_days` дней (`lib/updateLater.ts`).
 */
export function UpdateGate({ children }: { children: ReactNode }) {
  const [decision, setDecision] = useState<StartupDecision>('ok');
  // Пересчёт «Позже» по часам при возврате в приложение, даже если решение не поменялось.
  const [now, setNow] = useState(() => Date.now());
  // Нажатое «Позже» прячет плашку в этом сеансе, даже если хранилище не записалось, — на тот же срок, что запись.
  const [laterFor, setLaterFor] = useState<LaterRecord | null>(null);
  const logout = useAuthStore((s) => s.logout);

  useEffect(() => {
    let alive = true;
    const check = async () => {
      const next = await decideStartup(logout);
      if (!alive) return;
      setDecision(next);
      setNow(Date.now());
    };
    void check();
    // Настройки приезжают не мгновенно: проверяем и сразу, и через несколько
    // секунд — к этому моменту ответ сервера уже есть.
    const later = setTimeout(() => void check(), 4000);
    const unsubscribe = subscribeAppConfig(() => void check());

    let removeResume: (() => void) | undefined;
    if (isStoreBuild()) {
      const handle = CapacitorApp.addListener('appStateChange', ({ isActive }) => {
        if (isActive) void check();
      });
      removeResume = () => void handle.then((h) => h.remove()).catch(() => undefined);
    }

    return () => {
      alive = false;
      clearTimeout(later);
      unsubscribe();
      removeResume?.();
    };
  }, [logout]);

  if (decision === 'update-required') return <UpdateRequiredScreen />;

  const latest = decision === 'update-available' ? getLatestVersion(appPlatform()) : undefined;
  const days = getUpdateReminderDays();
  const showBanner =
    latest !== undefined && !laterHides(laterFor, latest, now, days) && shouldShowBanner(latest, now, days);

  return (
    <>
      {children}
      {showBanner && latest && (
        <UpdateBanner
          storeUrl={STORE_URL[appPlatform()]}
          onLater={() => {
            const at = Date.now();
            rememberLater(latest, at);
            setLaterFor({ version: latest, at });
            // ⚠️ И «сейчас» — тот же миг. `now` в состоянии — время последней проверки, оно раньше `at`, а отметка
            // «в будущем» для `laterHides` — истёкшая (часы назад): плашка оставалась до следующей проверки
            // (симулятор стенда 18.10.2026).
            setNow(at);
          }}
        />
      )}
    </>
  );
}
